// FILENAME: frontend/src/components/Dealer360View.jsx
import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShareIcon from '@mui/icons-material/Share';
import { alpha } from '@mui/material/styles';
import useApi from '../hooks/useApi';
import useShare from '../hooks/useShare';
import DealerHistoryMrtTable from './DealerHistoryMrtTable';
import DealerBacHistoryMrtTable from './DealerBacHistoryMrtTable';
import DealerSelectionsMrtTable from './DealerSelectionsMrtTable';
import {
  buildBacHistoryCounts,
  buildBacHistoryRows,
  buildProductHistoryCounts,
  buildProductHistoryRows,
  getBacHistoryPrograms,
  getProductHistoryPrograms,
} from './dealer-history/historyViewModel';
import { PROGRAM_CODE_MAP, isSelectionActive } from '../utils/dataNormalizer';
import { buildCalendarDateMeta, buildDateMeta } from '../utils/dateUtils';
import { normalizeSelectionBrands } from '../utils/brands';
import { getSearchHighlightColor } from '../utils/searchHighlights';
import {
  getApiPresenceStatusMeta,
  getEnrollmentStatusMeta,
} from '../utils/status';
import {
  resolveApiPresenceStatus,
  resolveEnrollmentLifecycleStatus,
} from '../utils/enrollmentEvidence';

const toDisplayDate = (value) => buildDateMeta(value).display;
const pickCalendarValue = (...values) =>
  values.find((value) => value !== null && value !== undefined && String(value).trim() !== '');

const buildStatusChip = (meta) => {
  if (!meta) return '';
  const color = meta.color && meta.color !== 'default' ? meta.color : 'default';
  const variant = color === 'default' ? 'outlined' : 'filled';
  return <Chip size="small" label={meta.label || 'Unknown'} color={color} variant={variant} />;
};

const Dealer360View = ({ bac, onBack, searchMatch = null }) => {
  const [tabValue, setTabValue] = useState(0);
  const [productHistoryFilter, setProductHistoryFilter] = useState('All Programs');
  const [productHistoryKind, setProductHistoryKind] = useState('All Changes');
  const [historySelectionFilter, setHistorySelectionFilter] = useState(null);
  const [bacHistoryFilter, setBacHistoryFilter] = useState('All Programs');
  const { useAllEnrollmentData, useDealerCleanHistory } = useApi();
  const { data: allProgramData, isLoading, isError, error } = useAllEnrollmentData();
  const { data: historyData = [], isLoading: isHistoryLoading } = useDealerCleanHistory(bac);
  const { share, isSupported: isShareSupported } = useShare();
  const highlightColor = useMemo(
    () => getSearchHighlightColor(searchMatch?.index),
    [searchMatch?.index]
  );

  const matchChipConfig = useMemo(() => {
    if (!searchMatch?.term) return null;
    const chipLabel = `Matched Term: ${searchMatch.term}`;
    const baseSx = highlightColor
      ? {
          backgroundColor: alpha(highlightColor, 0.16),
          borderColor: alpha(highlightColor, 0.45),
          borderWidth: 1,
          borderStyle: 'solid',
        }
      : {};
    return {
      label: chipLabel,
      sx: baseSx,
    };
  }, [highlightColor, searchMatch?.term]);

  const renderSearchMatchChip = useCallback(
    (extraProps = {}) => {
      if (!matchChipConfig) return null;
      return (
        <Chip
          size="small"
          {...extraProps}
          label={matchChipConfig.label}
          sx={{ ...(matchChipConfig.sx || {}), ...(extraProps.sx || {}) }}
        />
      );
    },
    [matchChipConfig]
  );

  const dealerData = useMemo(() => {
    if (isLoading || !allProgramData) return null;

    const enrollments = allProgramData
      .flatMap((program) =>
        (program.data || []).map((entry) => ({
          ...entry,
          programName: entry.programName || program.programName,
          programCode: entry.programCode || PROGRAM_CODE_MAP[program.programName] || entry.program,
          programLabel: entry.programLabel || entry.programName || program.programName,
        }))
      )
      .filter((entry) => entry.bac && entry.bac.toString() === bac);

    if (enrollments.length === 0) return { notFound: true };

    return {
      bac,
      dealerName: enrollments[0]?.dealerName || 'Unknown Dealer',
      enrollments,
    };
  }, [allProgramData, bac, isLoading]);

  const programList = useMemo(() => {
    if (!dealerData || dealerData.notFound) return [];
    return dealerData.enrollments.map((entry) => entry.programName);
  }, [dealerData]);

  const productHistoryPrograms = useMemo(() => getProductHistoryPrograms(historyData), [historyData]);
  const bacHistoryPrograms = useMemo(() => getBacHistoryPrograms(historyData), [historyData]);
  const productHistoryRows = useMemo(
    () => buildProductHistoryRows({ historyData, productHistoryFilter, productHistoryKind, historySelectionFilter }),
    [historyData, productHistoryFilter, productHistoryKind, historySelectionFilter]
  );
  const productHistoryCounts = useMemo(() => buildProductHistoryCounts(productHistoryRows), [productHistoryRows]);
  const bacHistoryRows = useMemo(
    () => buildBacHistoryRows({ historyData, bacHistoryFilter }),
    [historyData, bacHistoryFilter]
  );
  const bacHistoryCounts = useMemo(() => buildBacHistoryCounts(bacHistoryRows), [bacHistoryRows]);

  const handleShare = useCallback(() => {
    const dealerUrl = `${window.location.origin}/dealer/${bac}`;
    const shareData = {
      title: `${dealerData?.dealerName || 'Dealer'} - Enrollment Report`,
      text: `View enrollment details for ${dealerData?.dealerName || 'dealer'} (BAC: ${bac})`,
      url: dealerUrl,
    };
    share(shareData);
  }, [bac, dealerData?.dealerName, share]);

  const handleViewProductHistory = useCallback((row) => {
    const nextFilter = {
      programName: row?.programName || '',
      selectionReferenceId: String(row?.referenceId || '').trim(),
      selectionProductCode: String(row?.selectionProductCode || row?.productBrandCode || '').trim(),
      serviceItemName: row?.serviceItemName || '',
    };
    setProductHistoryFilter(nextFilter.programName || 'All Programs');
    setProductHistoryKind('All Changes');
    setHistorySelectionFilter(nextFilter);
    setTabValue(1);
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return <Alert severity="error">Error loading enrollment data: {error.message}</Alert>;
  }

  if (!dealerData || dealerData.notFound) {
    return (
      <Paper sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
          Back to Results
        </Button>
        <Typography variant="h4" gutterBottom>Dealer {bac}</Typography>
        <Alert severity="warning">Dealer data not found. They may not be enrolled in any tracked programs.</Alert>
      </Paper>
    );
  }

  const allProductSelections = dealerData.enrollments.flatMap((enrollment) =>
    (enrollment.productSelections || []).map((selection, index) => {
      const brands = normalizeSelectionBrands(selection?.brands ?? selection?.brandCodes ?? selection?.brandNames);
      const vendorProvidedMeta = buildCalendarDateMeta(selection?.vendorProvidedDate);
      const productSelectionMeta = buildCalendarDateMeta(
        pickCalendarValue(selection?.productSelectionDate, selection?.selectionDate)
      );
      const goLiveMeta = buildCalendarDateMeta(selection?.goLiveDate);
      const cancelMeta = buildCalendarDateMeta(selection?.cancelDate);
      const billingStartMeta = buildCalendarDateMeta(selection?.billingStartDate);
      const billingEndMeta = buildCalendarDateMeta(selection?.billingEndDate);
      const effectiveMeta = buildCalendarDateMeta(
        pickCalendarValue(
          selection?.effectiveDate,
          selection?.effectiveDt,
          selection?.effectiveAt,
          selection?.effectiveOn
        )
      );

      return {
        ...selection,
        id: `${enrollment.programName}-${selection.serviceItemName}-${index}`,
        programName: enrollment.programName,
        programCode: enrollment.programCode,
        selectionProductCode:
          selection?.selectionProductCode ||
          selection?.productCode ||
          selection?.productBrandCode ||
          selection?.referenceProductCode ||
          '',
        brandsDisplay: brands.display,
        brandsCodes: brands.codes,
        vendorProvidedDateDisplay: vendorProvidedMeta.display,
        vendorProvidedDateSort: vendorProvidedMeta.sort,
        productSelectionDateDisplay: productSelectionMeta.display,
        productSelectionDateSort: productSelectionMeta.sort,
        goLiveDateDisplay: goLiveMeta.display,
        goLiveDateSort: goLiveMeta.sort,
        cancelDateDisplay: cancelMeta.display,
        cancelDateSort: cancelMeta.sort,
        billingStartDateDisplay: billingStartMeta.display,
        billingStartDateSort: billingStartMeta.sort,
        billingEndDateDisplay: billingEndMeta.display,
        billingEndDateSort: billingEndMeta.sort,
        effectiveDateDisplay: effectiveMeta.display,
        effectiveDateSort: effectiveMeta.sort,
      };
    })
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>
          Back to Results
        </Button>
        {isShareSupported && (
          <Button startIcon={<ShareIcon />} onClick={handleShare} variant="outlined">
            Share Dealer
          </Button>
        )}
      </Box>
      {matchChipConfig && (
        <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {renderSearchMatchChip()}
        </Box>
      )}

      <Tabs
        value={tabValue}
        onChange={(_event, value) => setTabValue(value)}
        sx={{ mb: 3 }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        <Tab label="Overview" />
        <Tab label="Product History" />
        <Tab label="BAC History" />
      </Tabs>

      {tabValue === 0 && (
        <>
          <Typography variant="h4" gutterBottom>
            {dealerData.dealerName} ({dealerData.bac})
          </Typography>

          <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
            Program Enrollment Summary
          </Typography>

          <Grid container spacing={3}>
            {programList.map((programName) => {
              const enrollment = dealerData.enrollments.find((entry) => entry.programName === programName);
              return (
                <Grid item xs={12} sm={6} md={4} key={programName}>
                  <Card sx={{ height: '100%' }}>
                    <CardHeader title={programName} subheader={enrollment.programCode} />
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                          Status:
                        </Typography>
                        {buildStatusChip(getEnrollmentStatusMeta(resolveEnrollmentLifecycleStatus(enrollment)))}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                          API Presence:
                        </Typography>
                        {buildStatusChip(getApiPresenceStatusMeta(resolveApiPresenceStatus(enrollment)))}
                      </Box>
                      {enrollment.missingSince ? (
                        <Typography variant="body2">
                          <strong>Missing Since:</strong> {toDisplayDate(enrollment.missingSince)}
                        </Typography>
                      ) : null}
                      {enrollment.missingConfirmedAt ? (
                        <Typography variant="body2">
                          <strong>Missing Confirmed:</strong> {toDisplayDate(enrollment.missingConfirmedAt)}
                        </Typography>
                      ) : null}
                      <Typography variant="body2">
                        <strong>Enrolled:</strong> {toDisplayDate(enrollment.enrolledDt)}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Live Selections:</strong>{' '}
                        {(enrollment.productSelections || []).filter((p) => isSelectionActive(p)).length}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
            All Product Selections
          </Typography>
          <DealerSelectionsMrtTable
            rows={allProductSelections}
            isLoading={false}
            onViewHistory={handleViewProductHistory}
          />
        </>
      )}

      {tabValue === 1 && (
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              mb: 2,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h5">Product History</Typography>
              {renderSearchMatchChip()}
              {historySelectionFilter ? (
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`Filtered: ${historySelectionFilter.serviceItemName || historySelectionFilter.selectionProductCode || 'Selected Product'}${
                    historySelectionFilter.selectionReferenceId ? ` • ${historySelectionFilter.selectionReferenceId}` : ''
                  }`}
                  onDelete={() => {
                    setHistorySelectionFilter(null);
                    setProductHistoryFilter('All Programs');
                  }}
                />
              ) : null}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <FormControl sx={{ minWidth: 220 }} size="small">
                <InputLabel>Type</InputLabel>
                <Select value={productHistoryKind} label="Type" onChange={(event) => setProductHistoryKind(event.target.value)}>
                  <MenuItem value="All Changes">All Changes</MenuItem>
                  <MenuItem value="Selection Status Changes">Selection Status Changes</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ minWidth: 240 }} size="small">
                <InputLabel>Program</InputLabel>
                <Select value={productHistoryFilter} label="Program" onChange={(event) => setProductHistoryFilter(event.target.value)}>
                  {productHistoryPrograms.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          {isHistoryLoading ? (
            <CircularProgress />
          ) : (
            <DealerHistoryMrtTable rows={productHistoryRows} counts={productHistoryCounts} isLoading={isHistoryLoading} />
          )}
        </Paper>
      )}

      {tabValue === 2 && (
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              mb: 2,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h5">BAC History</Typography>
              {renderSearchMatchChip()}
            </Box>
            <FormControl sx={{ minWidth: 240 }} size="small">
              <InputLabel>Program</InputLabel>
              <Select value={bacHistoryFilter} label="Program" onChange={(event) => setBacHistoryFilter(event.target.value)}>
                {bacHistoryPrograms.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {isHistoryLoading ? (
            <CircularProgress />
          ) : (
            <DealerBacHistoryMrtTable rows={bacHistoryRows} counts={bacHistoryCounts} isLoading={isHistoryLoading} />
          )}
        </Paper>
      )}
    </Box>
  );
};

export default Dealer360View;
